import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listOfferApiService } from '../api/listOfferApiService';
import { listAvailableSubscriptions } from '../api/listAvailableSubscriptionsApiService';
import { listSubscriptionApiService } from '@/epics/subscriptionManagment/api/listSubscriptionApiService';
import type {
  OfferListItem,
  OfferSortField,
  OfferSortOrder,
} from '../types/offerListType';
import type { FilterOption, DateRange } from '@shared/components/FilterPopup';

const OFFER_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Aktywny', value: 'active', checked: false },
  { label: 'Nieaktywny', value: 'inactive', checked: false },
];

const MAX_SEARCH_LENGTH = 200;
const VALID_STATUSES = ['active', 'inactive', 'expired'] as const;

/** Trim and collapse multiple spaces to one. Used when sending to backend and syncing to URL. */
function normalizeSearchQuery(s: string): string {
  return s.trim().replace(/\s+/g, ' ');
}

function isValidStatus(s: string): boolean {
  return VALID_STATUSES.includes(s as (typeof VALID_STATUSES)[number]);
}

function sanitizeStatusParam(
  str: string | null | undefined
): string | undefined {
  if (!str) {
    return undefined;
  }
  const list = str
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s && isValidStatus(s));
  return list.length > 0 ? list.join(',') : undefined;
}

function statusListFromParam(str: string | null | undefined): string[] {
  if (!str) {
    return [];
  }
  return str
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface SubscriptionPlanFilterOption {
  id: string;
  name: string;
  checked: boolean;
}

export interface OfferListFilters {
  statusOptions: FilterOption[];
  dateRange: DateRange;
  subscriptionPlanOptions: SubscriptionPlanFilterOption[];
}

export function useOfferList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const urlPage = searchParams.get('page');
  const urlSearch = searchParams.get('search');
  const urlLimit = searchParams.get('limit');
  const urlStatus = sanitizeStatusParam(searchParams.get('status'));
  const urlValidityStart = searchParams.get('validityPeriodStart') ?? undefined;
  const urlValidityEnd = searchParams.get('validityPeriodEnd') ?? undefined;
  const urlPlanIds = searchParams.get('subscriptionPlanId') ?? undefined;
  const urlSortBy = searchParams.get('sortBy') ?? undefined;
  const urlSortOrder = searchParams.get('sortOrder') ?? undefined;

  const [offers, setOffers] = useState<OfferListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [page, setPage] = useState(() => {
    const n = urlPage ? parseInt(urlPage, 10) : 1;
    return n < 1 ? 1 : n;
  });
  const [limit, setLimit] = useState(() => {
    const n = urlLimit ? parseInt(urlLimit, 10) : 10;
    if (n > 100) {
      return 10;
    }
    if (n < 1) {
      return 10;
    }
    return n;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [search, setSearch] = useState(() => {
    const v = (urlSearch ?? '').trim();
    return v.length > MAX_SEARCH_LENGTH ? v.slice(0, MAX_SEARCH_LENGTH) : v;
  });
  const [limitError, setLimitError] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef(search);
  const prevDebouncedRef = useRef(debouncedSearch);
  const lastFetchKeyRef = useRef<string>('');
  const lastFetchTimeRef = useRef<number>(0);
  const DEDUPE_MS = 300;

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() => {
    const list = statusListFromParam(urlStatus ?? null);
    if (!urlStatus) {
      return OFFER_STATUS_OPTIONS.map((o) => ({ ...o }));
    }
    return [
      { label: 'Aktywny', value: 'active', checked: list.includes('active') },
      {
        label: 'Nieaktywny',
        value: 'inactive',
        checked: list.includes('inactive'),
      },
    ];
  });

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    startDate: urlValidityStart || undefined,
    endDate: urlValidityEnd || undefined,
  }));

  const [subscriptionPlanOptions, setSubscriptionPlanOptions] = useState<
    SubscriptionPlanFilterOption[]
  >([]);
  const [subscriptionPlansLoading, setSubscriptionPlansLoading] =
    useState(false);
  const plansLoadAttemptedRef = useRef(false);

  const VALID_SORT_BY: OfferSortField[] = [
    'offer_name',
    'start_date',
    'end_date',
  ];
  const DEFAULT_SORT_ORDER: OfferSortOrder = 'asc';

  // sortBy is undefined when no explicit sort is applied (default state)
  const [sortBy, setSortBy] = useState<OfferSortField | undefined>(() => {
    const urlSortByValue = urlSortBy as OfferSortField | undefined;
    return urlSortByValue && VALID_SORT_BY.includes(urlSortByValue)
      ? urlSortByValue
      : undefined;
  });

  const [sortOrder, setSortOrder] = useState<OfferSortOrder>(() => {
    const urlSortOrderValue = (urlSortOrder ?? '').toLowerCase();
    return urlSortOrderValue === 'desc' ? 'desc' : DEFAULT_SORT_ORDER;
  });

  const [appliedFilters, setAppliedFilters] = useState<OfferListFilters>(
    () => ({
      statusOptions: urlStatus
        ? [
            {
              label: 'Aktywny',
              value: 'active',
              checked: statusListFromParam(urlStatus).includes('active'),
            },
            {
              label: 'Nieaktywny',
              value: 'inactive',
              checked: statusListFromParam(urlStatus).includes('inactive'),
            },
          ]
        : OFFER_STATUS_OPTIONS.map((o) => ({ ...o })),
      dateRange:
        urlValidityStart || urlValidityEnd
          ? { startDate: urlValidityStart, endDate: urlValidityEnd }
          : {},
      subscriptionPlanOptions: [],
    })
  );

  // Load subscription plans on mount so they are ready when user opens the filter (no delay).
  useEffect(() => {
    if (plansLoadAttemptedRef.current) {
      return;
    }
    plansLoadAttemptedRef.current = true;
    let cancelled = false;
    setSubscriptionPlansLoading(true);

    const toBaseOpts = (rawPlans: unknown[]): SubscriptionPlanFilterOption[] =>
      rawPlans
        .map((p) => {
          const plan = p as {
            id?: string;
            planName?: string;
            plan_name?: string;
            name?: string;
          };
          return {
            id: plan?.id ?? '',
            name: plan?.planName ?? plan?.plan_name ?? plan?.name ?? '',
            checked: false,
          };
        })
        .filter((p) => p.id && p.name);

    const extractPlansArray = (res: unknown): unknown[] => {
      if (!res || typeof res !== 'object') {
        return [];
      }
      const r = res as Record<string, unknown>;
      if (Array.isArray(r['data'])) {
        return r['data'];
      }
      if (Array.isArray(r['subscriptions'])) {
        return r['subscriptions'];
      }
      if (Array.isArray(r['plans'])) {
        return r['plans'];
      }
      return [];
    };

    const fetchPlans = async (): Promise<SubscriptionPlanFilterOption[]> => {
      // Primary: list subscriptions (all plans). Same API as Subscription list page.
      const res = await listSubscriptionApiService.listSubscriptions({
        page: 1,
        limit: 100,
      });
      const rawPlans = extractPlansArray(res);
      const opts = toBaseOpts(rawPlans);
      if (opts.length > 0) {
        return opts;
      }
      // Fallback: available subscriptions (filter_id=null). Same API as Create/Edit offer.
      const fallback = await listAvailableSubscriptions();
      const fallbackPlans = extractPlansArray(fallback);
      return toBaseOpts(fallbackPlans);
    };

    fetchPlans()
      .then((baseOpts) => {
        if (cancelled) {
          return;
        }
        setSubscriptionPlansLoading(false);
        const planIdsList = urlPlanIds
          ? urlPlanIds
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        const opts: SubscriptionPlanFilterOption[] = baseOpts.map((p) => ({
          ...p,
          checked: planIdsList.includes(p.id),
        }));
        setSubscriptionPlanOptions(opts);
        if (planIdsList.length > 0) {
          setAppliedFilters((f) => ({
            ...f,
            subscriptionPlanOptions: opts,
          }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubscriptionPlansLoading(false);
          setSubscriptionPlanOptions([]);
        }
        plansLoadAttemptedRef.current = false;
      });
    return () => {
      cancelled = true;
      // Reset so Strict Mode re-run can fetch (prevents "never loads" bug)
      plansLoadAttemptedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync from URL (e.g. back/forward)
  useEffect(() => {
    const curPage = searchParams.get('page');
    const curSearch = (searchParams.get('search') ?? '').trim();
    const curLimit = searchParams.get('limit');
    const curStatus = sanitizeStatusParam(searchParams.get('status'));
    const curStart = searchParams.get('validityPeriodStart') ?? undefined;
    const curEnd = searchParams.get('validityPeriodEnd') ?? undefined;
    const curPlanIds = searchParams.get('subscriptionPlanId') ?? undefined;
    const curSortBy = searchParams.get('sortBy') as OfferSortField | undefined;
    const curSortOrder = searchParams.get('sortOrder') ?? undefined;

    // Skip sync when only cols changed (e.g. column customization) - no API refetch needed
    const urlPageNum = curPage ? parseInt(curPage, 10) : 1;
    const urlLimitNum = curLimit ? parseInt(curLimit, 10) : 10;
    const planIdsMatch =
      (curPlanIds ?? '') ===
      appliedFilters.subscriptionPlanOptions
        .filter((o) => o.checked)
        .map((o) => o.id)
        .join(',');
    const statusMatch =
      (curStatus ?? '') ===
      appliedFilters.statusOptions
        .filter((o) => o.checked)
        .map((o) => o.value)
        .join(',');
    if (
      (curPage ? urlPageNum >= 1 && urlPageNum === page : page === 1) &&
      (curLimit
        ? urlLimitNum >= 1 && urlLimitNum <= 100 && urlLimitNum === limit
        : limit === 10) &&
      (curSearch.length > MAX_SEARCH_LENGTH
        ? curSearch.slice(0, MAX_SEARCH_LENGTH) === search.trim()
        : curSearch === search.trim()) &&
      statusMatch &&
      (appliedFilters.dateRange.startDate ?? '') === (curStart ?? '') &&
      (appliedFilters.dateRange.endDate ?? '') === (curEnd ?? '') &&
      planIdsMatch &&
      (curSortBy ? sortBy === curSortBy : !sortBy) &&
      (curSortOrder === 'asc' || curSortOrder === 'desc'
        ? sortOrder === curSortOrder
        : sortOrder === DEFAULT_SORT_ORDER)
    ) {
      return;
    }

    isSyncingFromUrl.current = true;

    if (curPage) {
      const n = parseInt(curPage, 10);
      if (n >= 1 && n !== page) {
        setPage(n);
      }
    } else if (page !== 1) {
      setPage(1);
    }

    if (curLimit) {
      const n = parseInt(curLimit, 10);
      if (n >= 1 && n <= 100 && n !== limit) {
        setLimit(n);
      }
    } else if (limit !== 10) {
      setLimit(10);
    }

    const searchVal =
      curSearch.length > MAX_SEARCH_LENGTH
        ? curSearch.slice(0, MAX_SEARCH_LENGTH)
        : curSearch;
    if (searchVal !== search.trim()) {
      setSearch(searchVal);
    }

    if (curStatus) {
      const list = statusListFromParam(curStatus);
      setStatusOptions([
        { label: 'Aktywny', value: 'active', checked: list.includes('active') },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: list.includes('inactive'),
        },
      ]);
    } else {
      setStatusOptions(OFFER_STATUS_OPTIONS.map((o) => ({ ...o })));
    }

    setDateRange({ startDate: curStart, endDate: curEnd });

    if (curSortBy && VALID_SORT_BY.includes(curSortBy)) {
      setSortBy(curSortBy);
    } else {
      setSortBy(undefined);
    }
    if (curSortOrder === 'asc' || curSortOrder === 'desc') {
      setSortOrder(curSortOrder);
    } else {
      setSortOrder(DEFAULT_SORT_ORDER);
    }

    if (curPlanIds) {
      const ids = curPlanIds.split(',').map((s) => s.trim());
      setSubscriptionPlanOptions((prev) =>
        prev.length
          ? prev.map((p) => ({ ...p, checked: ids.includes(p.id) }))
          : prev
      );
      setAppliedFilters((f) => ({
        ...f,
        subscriptionPlanOptions: f.subscriptionPlanOptions.length
          ? f.subscriptionPlanOptions.map((p) => ({
              ...p,
              checked: ids.includes(p.id),
            }))
          : f.subscriptionPlanOptions,
      }));
    } else {
      setSubscriptionPlanOptions((prev) =>
        prev.map((p) => ({ ...p, checked: false }))
      );
      setAppliedFilters((f) => ({
        ...f,
        subscriptionPlanOptions: f.subscriptionPlanOptions.map((p) => ({
          ...p,
          checked: false,
        })),
      }));
    }

    setAppliedFilters((f) => ({
      ...f,
      statusOptions: curStatus
        ? [
            {
              label: 'Aktywny',
              value: 'active',
              checked: statusListFromParam(curStatus).includes('active'),
            },
            {
              label: 'Nieaktywny',
              value: 'inactive',
              checked: statusListFromParam(curStatus).includes('inactive'),
            },
          ]
        : OFFER_STATUS_OPTIONS.map((o) => ({ ...o })),
      dateRange:
        curStart || curEnd ? { startDate: curStart, endDate: curEnd } : {},
      ...(!curPlanIds && {
        subscriptionPlanOptions: f.subscriptionPlanOptions.map((p) => ({
          ...p,
          checked: false,
        })),
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, statusOptions, dateRange, appliedFilters]);

  // Push state to URL
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }
    const params = new URLSearchParams();
    const prevNormalized = normalizeSearchQuery(prevSearchRef.current);
    const currNormalized = normalizeSearchQuery(search);
    if (prevNormalized !== currNormalized) {
      prevSearchRef.current = search;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }
    params.set('limit', limit.toString());
    if (currNormalized) {
      params.set('search', currNormalized);
    }
    const statusStr = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    if (statusStr) {
      params.set('status', statusStr);
    }
    if (appliedFilters.dateRange.startDate) {
      params.set('validityPeriodStart', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange.endDate) {
      params.set('validityPeriodEnd', appliedFilters.dateRange.endDate);
    }
    const planIds = appliedFilters.subscriptionPlanOptions
      .filter((o) => o.checked)
      .map((o) => o.id)
      .join(',');
    if (planIds) {
      params.set('subscriptionPlanId', planIds);
    }

    // Only add sort params if user has explicitly sorted
    if (sortBy) {
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
    }
    // Preserve column visibility (cols) so offer list column customization persists in URL
    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }
    const newStr = params.toString();
    const curStr = new URLSearchParams(searchParams.toString()).toString();
    if (newStr !== curStr) {
      setSearchParams(params, { replace: false });
    }
  }, [
    page,
    limit,
    search,
    appliedFilters,
    sortBy,
    sortOrder,
    setSearchParams,
    searchParams,
  ]);

  const fetchOffers = useCallback(async () => {
    const statusStr = appliedFilters.statusOptions
      .filter((o) => o.checked)
      .map((o) => o.value)
      .join(',');
    const planIdsFromFilters = appliedFilters.subscriptionPlanOptions
      .filter((o) => o.checked)
      .map((o) => o.id)
      .join(',');
    // Use urlPlanIds when restoring from URL (plans may not have loaded yet)
    const planIds =
      planIdsFromFilters ||
      (urlPlanIds
        ? urlPlanIds
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .join(',')
        : '');
    const normalizedSearch = normalizeSearchQuery(debouncedSearch);
    const fetchKey = [
      page,
      limit,
      normalizedSearch,
      statusStr,
      appliedFilters.dateRange.startDate ?? '',
      appliedFilters.dateRange.endDate ?? '',
      planIds,
      sortBy,
      sortOrder,
    ].join('|');
    const now = Date.now();
    if (
      lastFetchKeyRef.current === fetchKey &&
      now - lastFetchTimeRef.current < DEDUPE_MS
    ) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;
    lastFetchTimeRef.current = now;

    try {
      setLoading(true);
      const response = await listOfferApiService.listOffers({
        page,
        limit,
        ...(normalizedSearch && { search: normalizedSearch }),
        ...(statusStr && { status: statusStr }),
        ...(appliedFilters.dateRange.startDate && {
          validityPeriodStart: appliedFilters.dateRange.startDate,
        }),
        ...(appliedFilters.dateRange.endDate && {
          validityPeriodEnd: appliedFilters.dateRange.endDate,
        }),
        ...(planIds && { subscriptionPlanId: planIds }),
        ...(sortBy && { sortBy, sortOrder }),
      });
      if (response.forbidden) {
        setAccessError(true);
        setLoading(false);
        return;
      }
      setOffers(response.data);
      setTotalCount(response.totalCount);
      const hasNoFilters =
        !normalizedSearch &&
        !statusStr &&
        !appliedFilters.dateRange.startDate &&
        !appliedFilters.dateRange.endDate &&
        !planIds;
      setIsNoFilterFetch(hasNoFilters);
    } catch {
      setOffers([]);
      setTotalCount(0);
      setIsNoFilterFetch(false); // Don't show "Brak ofert." on API error; state is unknown
    } finally {
      setLoading(false);
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
    // isInitialLoad intentionally omitted: it's only used in finally to clear the flag.
    // Including it would recreate fetchOffers when the first load completes and retrigger
    // the effect, causing a second fetch and a visible loading flicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearch, appliedFilters, sortBy, sortOrder]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevDebouncedRef.current = debouncedSearch;
      return;
    }
    const prev = prevDebouncedRef.current.trim();
    const curr = debouncedSearch.trim();
    if (prev !== curr && page !== 1) {
      setPage(1);
    }
    prevDebouncedRef.current = debouncedSearch;
  }, [debouncedSearch, page]);

  const hasFilters =
    statusOptions.some((o) => o.checked) ||
    !!dateRange.startDate ||
    !!dateRange.endDate ||
    subscriptionPlanOptions.some((o) => o.checked);
  const isSearchClearing =
    !search.trim() && debouncedSearch.trim() !== '' && !hasFilters;
  const prevDebouncedTrimmed = prevDebouncedRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !hasFilters;

  useEffect(() => {
    // Skip fetch during clear transition: search is empty but debouncedSearch
    // hasn't caught up yet. Prevents redundant fetch (and extra shimmer) when
    // sync-from-URL updates appliedFilters before debounce fires.
    if (isSearchClearing) {
      return;
    }
    fetchOffers();
  }, [fetchOffers, isSearchClearing]);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (newLimit > 100) {
      setLimitError('Limit nie może przekraczać 100.');
      return;
    }
    if (newLimit < 1) {
      setLimitError('Limit musi być większy niż 0.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const setSearchWithReset = useCallback(
    (value: string) => {
      if (value.length > MAX_SEARCH_LENGTH) {
        return;
      }
      if (
        !isSyncingFromUrl.current &&
        normalizeSearchQuery(value) !== normalizeSearchQuery(search)
      ) {
        setPage(1);
      }
      setSearch(value);
    },
    [search]
  );

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      statusOptions,
      dateRange,
      subscriptionPlanOptions,
    });
    setPage(1);
  }, [statusOptions, dateRange, subscriptionPlanOptions]);

  const handleClearFilters = useCallback(() => {
    const defStatus = OFFER_STATUS_OPTIONS.map((o) => ({ ...o }));
    const defDate: DateRange = {};
    const defPlans = subscriptionPlanOptions.map((p) => ({
      ...p,
      checked: false,
    }));
    setStatusOptions(defStatus);
    setDateRange(defDate);
    setSubscriptionPlanOptions(defPlans);
    const hadFilters =
      appliedFilters.statusOptions.some((o) => o.checked) ||
      appliedFilters.dateRange.startDate ||
      appliedFilters.dateRange.endDate ||
      appliedFilters.subscriptionPlanOptions.some((o) => o.checked);
    if (hadFilters) {
      setAppliedFilters({
        statusOptions: defStatus,
        dateRange: defDate,
        subscriptionPlanOptions: defPlans,
      });
      setPage(1);
    }
  }, [appliedFilters, subscriptionPlanOptions]);

  const handleSort = useCallback(
    (columnKey: string) => {
      const field: OfferSortField | undefined =
        columnKey === 'offerName'
          ? 'offer_name'
          : columnKey === 'validityPeriod'
            ? 'start_date'
            : undefined;
      if (!field) {
        return;
      }

      // Show loading shimmer when sorting
      setLoading(true);

      setSortBy(field);
      setSortOrder((prev) =>
        sortBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'
      );
      setPage(1);
    },
    [sortBy]
  );

  return {
    offers,
    loading,
    accessError,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    debouncedSearch,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch: setSearchWithReset,
    statusOptions,
    setStatusOptions,
    dateRange,
    setDateRange,
    subscriptionPlanOptions,
    setSubscriptionPlanOptions,
    subscriptionPlansLoading,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchOffers: fetchOffers,
    sortBy,
    sortOrder,
    handleSort,
  };
}
